import { obfuscatedCoordinates } from './maps';

/**
 * Location privacy utilities for implementing Airbnb-style location privacy
 * where approximate locations are shown publicly and exact addresses are revealed
 * only after booking confirmation.
 */

/**
 * Extract neighborhood/area information from a full address
 * This attempts to show a general area instead of the exact address
 * 
 * @param {string} fullAddress - The complete address
 * @returns {string} - Approximate address showing general area
 */
export const getApproximateAddress = (fullAddress) => {
  if (!fullAddress) return '';
  
  // Split address by commas to get components
  const addressParts = fullAddress.split(',').map(part => part.trim());
  
  if (addressParts.length <= 2) {
    // If address is already short, return as is
    return fullAddress;
  }
  
  // For most addresses, we want to show city/neighborhood + state/country
  // but hide the specific street address
  if (addressParts.length >= 3) {
    // Skip the first part (street address) and show the rest
    return addressParts.slice(1).join(', ');
  }
  
  return fullAddress;
};

/**
 * Create an approximate location object that hides exact address details
 * but shows general area information
 * 
 * @param {Object} exactLocation - Original location object with address and coordinates
 * @param {string} exactLocation.address - Full exact address
 * @param {Object} exactLocation.origin - Exact coordinates (LatLng)
 * @param {string} listingId - Listing ID for consistent obfuscation
 * @param {number} fuzzyOffset - Offset in meters for coordinate obfuscation (default: 500)
 * @returns {Object} - Approximate location object
 */
export const createApproximateLocation = (exactLocation, listingId, fuzzyOffset = 500) => {
  if (!exactLocation) return null;
  
  const { address, origin } = exactLocation;
  
  return {
    address: getApproximateAddress(address),
    origin: origin ? obfuscatedCoordinates(origin, fuzzyOffset, listingId) : null,
    isApproximate: true
  };
};

/**
 * Determine if exact location should be revealed based on booking status
 * 
 * @param {Object} transaction - Transaction object
 * @param {Object} currentUser - Current user object
 * @param {Object} process - Transaction process object
 * @returns {boolean} - Whether to show exact location
 */
export const shouldRevealExactLocation = (transaction, currentUser, process) => {
  if (!transaction || !currentUser || !process) {
    return false;
  }
  
  // Always show exact location to the listing owner/provider
  const isProvider = transaction.provider?.id?.uuid === currentUser.id?.uuid;
  if (isProvider) {
    return true;
  }
  
  // Show exact location to customer only after booking is accepted/confirmed
  const isCustomer = transaction.customer?.id?.uuid === currentUser.id?.uuid;
  if (!isCustomer) {
    return false;
  }
  
  // Check if booking has been accepted/confirmed
  // This depends on your transaction process states
  const acceptedStates = [
    'accepted',
    'confirmed', 
    'delivered',
    'completed'
  ];
  
  const currentState = process.getState(transaction);
  return acceptedStates.some(state => 
    process.states[state.toUpperCase()] === currentState
  );
};

/**
 * Get the appropriate location data based on privacy rules
 * 
 * @param {Object} listing - Listing object
 * @param {Object} transaction - Transaction object (optional)
 * @param {Object} currentUser - Current user object (optional)
 * @param {Object} process - Transaction process object (optional)
 * @returns {Object} - Location object with appropriate privacy level
 */
export const getLocationForDisplay = (listing, transaction = null, currentUser = null, process = null) => {
  if (!listing?.attributes) {
    return null;
  }
  
  const { geolocation, publicData } = listing.attributes;
  const exactLocation = publicData?.location;
  
  if (!exactLocation?.address || !geolocation) {
    return null;
  }
  
  // If no transaction context, always show approximate location
  if (!transaction || !currentUser || !process) {
    return createApproximateLocation(
      { address: exactLocation.address, origin: geolocation },
      listing.id?.uuid
    );
  }
  
  // Check if exact location should be revealed
  if (shouldRevealExactLocation(transaction, currentUser, process)) {
    return {
      address: exactLocation.address,
      building: exactLocation.building,
      origin: geolocation,
      isApproximate: false
    };
  }
  
  // Default to approximate location
  return createApproximateLocation(
    { address: exactLocation.address, origin: geolocation },
    listing.id?.uuid
  );
};

/**
 * Create a location privacy notice message
 * 
 * @param {boolean} isApproximate - Whether the location is approximate
 * @returns {string} - Privacy notice message key for internationalization
 */
export const getLocationPrivacyNotice = (isApproximate) => {
  return isApproximate 
    ? 'LocationPrivacy.approximateLocationNotice'
    : 'LocationPrivacy.exactLocationNotice';
};

/**
 * Get location privacy status message for transaction page
 * 
 * @param {boolean} shouldShowExact - Whether exact location should be shown
 * @param {boolean} isProvider - Whether current user is the provider
 * @returns {string} - Status message key for internationalization
 */
export const getLocationStatusMessage = (shouldShowExact, isProvider) => {
  if (isProvider) {
    return 'LocationPrivacy.providerViewsExactLocation';
  }
  
  return shouldShowExact 
    ? 'LocationPrivacy.exactLocationRevealed'
    : 'LocationPrivacy.exactLocationPendingBooking';
};
